import type {
  FlightPlayerScoresFragment,
  HoleScoreFragment,
} from '../../../__generated__/hooks'
import { getHoleStrokes } from '../../../util/scoring'
import ScoreCell from './ScoreCell'

/**
 * One row per hole — hole number / par / index in column 1, then a
 * `ScoreCell` per flight entry.
 *
 * `cellSubLabel` is the best-ball escape hatch: when supplied (team
 * view), each cell stamps the contributing player's initials underneath
 * the number. Strokes-given is suppressed in that case because team rows
 * don't have a meaningful per-hole strokes value.
 */
const HoleRow = ({
  hole,
  flight,
  cellSubLabel,
  showStableford,
}: {
  hole: HoleScoreFragment
  flight: FlightPlayerScoresFragment[]
  cellSubLabel?: (entryId: string, holeNumber: number) => string | null
  showStableford?: boolean
}) => (
  <tr
    className="border-t border-ink-100"
    style={{ background: 'var(--paper)' }}
  >
    <th
      scope="row"
      className="text-left px-2 py-1 align-middle"
      style={{ background: 'var(--paper)' }}
    >
      <div className="font-mono num text-sm font-bold text-ink-900 leading-tight">
        {hole.hole.number}
      </div>
      <div className="font-mono num text-[10px] text-ink-500 leading-tight mt-0.5">
        Par {hole.hole.par}
        <span className="text-ink-300 mx-0.5">/</span>
        {hole.hole.index}
      </div>
    </th>
    {flight.map((entry) => {
      const cellScore = (entry.scores ?? []).find(
        (s) => s.hole.number === hole.hole.number
      )
      const subLabel = cellSubLabel?.(entry.id, hole.hole.number) ?? null
      // Recompute per-hole strokes from the entry's total. The score-row
      // `strokes` getter on the API returns 0 for Scramble team scores
      // (team.polfEvent isn't loaded on score.team). See util/scoring.
      const computedStrokes =
        entry.strokes > 0 ? getHoleStrokes(entry.strokes, hole.hole.index) : 0
      return (
        <ScoreCell
          key={entry.id}
          score={cellScore}
          par={hole.hole.par}
          strokes={subLabel != null ? 0 : computedStrokes}
          subLabel={subLabel}
          showStableford={showStableford}
        />
      )
    })}
  </tr>
)

export default HoleRow
